import type { CreateOrganization, UpdateOrganization } from '@monorepo/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/lib/toast'
import { organizationsService } from '../services/organizationsService'

interface ApiError {
  response?: {
    data?: {
      error?: {
        message?: string
      }
      message?: string
    }
  }
  message?: string
}

const ORGANIZATIONS_QUERY_KEY = 'organizations'

// Query hooks
export function useOrganizations() {
  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY],
    queryFn: () => organizationsService.getOrganizations(),
  })
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: [ORGANIZATIONS_QUERY_KEY, id],
    queryFn: () => organizationsService.getOrganization(id!),
    enabled: !!id,
  })
}

// Mutation hooks
export function useCreateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateOrganization) =>
      organizationsService.createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] })
      toast.success('Organization created successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create organization'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganization }) =>
      organizationsService.updateOrganization(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] })
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATIONS_QUERY_KEY, variables.id],
      })
      // Invalidate customer queries since organization data is displayed in customer details
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Organization updated successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update organization'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => organizationsService.deleteOrganization(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ORGANIZATIONS_QUERY_KEY] })
      toast.success('Organization deleted successfully')
    },
    onError: (error: ApiError) => {
      const errorMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete organization'
      toast.error(errorMessage)
    },
  })
}
